import React from "react";

import {
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Grid,
  FormControl,
  FormHelperText,
  Checkbox,
  Select,
  InputLabel,
  Tooltip,
  ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DownloadIcon from "mdi-react/DownloadIcon";
import FileIcon from "mdi-react/FileIcon";
import FileImageIcon from "mdi-react/FileImageIcon";
import FilePdfBoxIcon from "mdi-react/FilePdfBoxIcon";
import { Controller, useWatch } from "react-hook-form";

import { msgs } from "@config/conf";

import ConditionalFieldRenderer from "./ConditionalFieldRenderer";
import RepeatingGroup from "./RepeatingGroup";
import { getPluralSuffix, isValidNormalText } from "../helpers";

const FormBuilder = ({ formConfig = [], form, submitErrors }) => {
  const {
    control,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = form;
  const watchedValues = useWatch({ control });

  const ALL_VALUE = "__ALL__";

  const useStyles = makeStyles((theme) => ({
    bigTooltip: {
      backgroundColor: "#1976d2", // custom background color
      color: "#fff", // text color
      fontSize: theme.typography.pxToRem(16), // bigger font size
      borderRadius: 8, // rounded corners
      padding: "8px 12px",
    },
    bigArrow: {
      color: "#1976d2", // arrow color to match background
    },
    container: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "8px",
    },
    uploadLabel: {
      display: "inline-block",
      padding: "8px 16px",
      backgroundColor: "#1976d2",
      color: "#fff",
      borderRadius: "4px",
      cursor: "pointer",
      marginBottom: "8px",
    },
    fileDisplay: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "8px",
    },
    fileInfo: {
      display: "flex",
      alignItems: "center",
    },
    fileIcon: {
      fontSize: "24px",
      marginRight: "8px",
    },
    downloadButton: {
      marginRight: "8px",
      padding: "1px",
      backgroundColor: "#4caf50",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
    deleteButton: {
      padding: "5px 6px",
      backgroundColor: "#f44336",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
    formControl: {
      padding: "4px 0",
      marginBottom: "8px",
      border: "none",
    },
    labelText: {
      fontSize: "14px",
      marginBottom: "4px",
      color: "inherit",
    },
    labelError: {
      color: "red",
    },
    helperText: {
      marginTop: "2px",
      fontSize: "11px",
      lineHeight: "1.2",
      color: "red",
    },
  }));

  const classes = useStyles();

  const renderWithTooltip = (field, content) => {
    return field.tooltip ? (
      <Tooltip
        title={field.tooltip}
        arrow
        classes={{
          tooltip: classes.bigTooltip,
          arrow: classes.bigArrow,
        }}
      >
        <div>{content}</div>
      </Tooltip>
    ) : (
      content
    );
  };

  const shouldRender = (field) => {
    if (!field.conditionalOn) return true;
    const { field: condField, value } = field.conditionalOn;
    return getValues(condField) === value;
  };

  // const onSubmit = (data) => {
  //     console.log("Form Submitted:", data);
  // };

  return (
    <>
      <Grid container spacing={2}>
        {formConfig.map((field) => {
          if (!shouldRender(field)) return null;

          const renderField = () => {
            switch (field.type) {
              case "text":
              case "number":
              case "date":
                return (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={{
                      required: field.required ? msgs.fieldError : false,
                      validate: (value) => {
                        if (
                          field.validator === "normalText" &&
                          !isValidNormalText(value)
                        ) {
                          return msgs.normalTextError;
                        }
                        return true;
                      },
                      pattern:
                        field.validator === "ssn"
                          ? {
                              value:
                                /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
                              message:
                                "Invalid SSN format (e.g., 123-45-6789) and must not start with 000, 666, or 900–999",
                            }
                          : undefined,
                    }}
                    render={({ field: controllerField }) => (
                      <TextField
                        {...controllerField}
                        label={field.label}
                        type={field.type}
                        fullWidth
                        margin="normal"
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                        InputLabelProps={
                          field.type === "date" ? { shrink: true } : {}
                        }
                        onChange={(e) => {
                          let value = e.target.value;

                          // Custom maxLength validation
                          if (
                            field.maxLength &&
                            value.length > field.maxLength
                          ) {
                            setError(field.name, {
                              type: "maxLength",
                              message: `Maximum length of ${field.maxLength} character${getPluralSuffix(field.maxLength)} exceeded.`,
                            });
                          } else {
                            clearErrors(field.name);
                          }

                          if (field.validator === "ssn") {
                            let rawValue = value.replace(/\D/g, ""); // Remove non-digit characters

                            let formattedValue = rawValue;
                            if (rawValue.length > 3 && rawValue.length <= 5) {
                              formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3)}`;
                            } else if (rawValue.length > 5) {
                              formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3, 5)}-${rawValue.slice(5, 9)}`;
                            }

                            controllerField.onChange(formattedValue);
                          } else {
                            controllerField.onChange(value); // Allow normal text input
                          }
                        }}
                      />
                    )}
                  />
                );

              case "staticTextBold":
                return (
                  <Typography variant="body1" style={{ padding: "8px 0" }}>
                    <strong>{field.value}</strong>
                  </Typography>
                );

              case "staticText":
                return (
                  <Typography variant="body1" style={{ padding: "8px 0" }}>
                    {field.value}
                  </Typography>
                );

              case "select":
                return renderWithTooltip(
                  field,
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={{
                      required: field.required ? msgs.fieldError : false,
                    }}
                    render={({ field: controllerField }) => (
                      <TextField
                        {...controllerField}
                        select
                        label={field.label}
                        fullWidth
                        margin="normal"
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                      >
                        {field.options.map((opt) =>
                          typeof opt === "string" ? (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ) : (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          )
                        )}
                      </TextField>
                    )}
                  />
                );
              case "multiSelect":
                return (
                  <Controller
                    name={field.name}
                    control={control}
                    defaultValue={field.isMulti ? [] : ""}
                    rules={{
                      required: field.required ? msgs.fieldError : false,
                    }}
                    render={({
                      field: { value = field.isMulti ? [] : "", onChange },
                    }) => {
                      // map value -> label (helps renderValue show labels, not raw values)
                      const valueToLabel = new Map(
                        field.options.map((o) => [o.value, o.label])
                      );

                      const handleSelect = (event) => {
                        const selected = event.target.value;

                        if (!field.isMulti) {
                          onChange(selected); // plain single select
                          return;
                        }

                        // MULTI:
                        if (selected.includes(ALL_VALUE)) {
                          const allVals = field.options.map((o) => o.value);
                          const isAllSelected =
                            Array.isArray(value) &&
                            value.length === allVals.length;
                          onChange(isAllSelected ? [] : allVals);
                        } else {
                          onChange(selected); // MUI already gives merged array for multiple
                        }
                      };

                      const isMulti = !!field.isMulti;
                      const selectedArray = isMulti
                        ? Array.isArray(value)
                          ? value
                          : []
                        : [value];

                      return (
                        <FormControl
                          fullWidth
                          error={!!errors?.[field.name]}
                          margin="normal"
                        >
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            multiple={isMulti}
                            value={value}
                            onChange={handleSelect}
                            displayEmpty
                            renderValue={(selected) => {
                              // nothing selected
                              if (
                                (isMulti &&
                                  (!selected || selected.length === 0)) ||
                                (!isMulti && !selected)
                              ) {
                                return "";
                              }
                              // show comma-separated LABELS for either single or multi
                              const vals = isMulti ? selected : [selected];
                              const labels = vals
                                .map((v) => valueToLabel.get(v))
                                .filter(Boolean);
                              return labels.join(", ");
                            }}
                            MenuProps={{
                              getContentAnchorEl: null, // MUI v4: anchor dropdown below field
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              PaperProps: {
                                style: { maxHeight: 320, width: 320 },
                              },
                              variant: "menu",
                              disableScrollLock: true,
                            }}
                          >
                            {isMulti && (
                              <MenuItem value={ALL_VALUE} dense>
                                <Checkbox
                                  color="primary"
                                  checked={
                                    selectedArray.length > 0 &&
                                    selectedArray.length ===
                                      field.options.length
                                  }
                                  indeterminate={
                                    selectedArray.length > 0 &&
                                    selectedArray.length < field.options.length
                                  }
                                />
                                Select All
                              </MenuItem>
                            )}

                            {field.options.map(
                              ({ value: optionValue, label: optionLabel }) => (
                                <MenuItem
                                  key={optionValue}
                                  value={optionValue}
                                  dense
                                >
                                  {isMulti && (
                                    <Checkbox
                                      checked={
                                        selectedArray.indexOf(optionValue) > -1
                                      }
                                      color="primary"
                                    />
                                  )}
                                  <ListItemText primary={optionLabel} />
                                </MenuItem>
                              )
                            )}
                          </Select>

                          <FormHelperText>
                            {errors?.[field.name]?.message || "\u00A0"}
                          </FormHelperText>
                        </FormControl>
                      );
                    }}
                  />
                );
              case "radio":
                return renderWithTooltip(
                  field,
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={{
                      required: field.required ? msgs.fieldError : false,
                    }}
                    render={({ field: controllerField }) => (
                      <FormControl
                        component="fieldset"
                        error={!!errors?.[field.name]}
                        className={classes.formControl}
                      >
                        <Typography
                          className={
                            errors?.[field.name]
                              ? `${classes.labelText} ${classes.labelError}`
                              : classes.labelText
                          }
                        >
                          {field.label}
                        </Typography>
                        <RadioGroup
                          {...controllerField}
                          value={controllerField.value || ""}
                          row
                        >
                          {field.options.map((opt) =>
                            typeof opt === "string" ? (
                              <FormControlLabel
                                key={opt}
                                value={opt}
                                control={<Radio />}
                                label={opt}
                              />
                            ) : (
                              <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                control={<Radio />}
                                label={opt.label}
                              />
                            )
                          )}
                        </RadioGroup>
                        {errors?.[field.name] && (
                          <FormHelperText className={classes.helperText}>
                            {errors[field.name].message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                );
              case "file":
                return (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (field.required) {
                          if (!value || !value.base64) {
                            return msgs.fieldError;
                          }

                          const mimeMatch = /^data:(.*);base64,/.exec(
                            value.base64
                          );
                          const mimeType = mimeMatch?.[1] || value.contentType;

                          const allowedTypes = [
                            "application/pdf",
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                            "image/gif",
                            "image/bmp",
                          ];

                          if (!allowedTypes.includes(mimeType)) {
                            return "Only PDF, JPEG, JPG, PNG, GIF, BMP files are allowed.";
                          }

                          return true;
                        }

                        return true;
                      },
                    }}
                    render={({ field: controllerField }) => {
                      const value = controllerField.value;
                      const hasFile = value?.base64;

                      const getFileIcon = (mimeType) => {
                        if (mimeType.includes("pdf"))
                          return <FilePdfBoxIcon size={24} />;
                        if (mimeType.includes("image"))
                          return <FileImageIcon size={24} />;
                        return <FileIcon size={24} />;
                      };

                      return (
                        <div
                          className="file-upload"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                const base64 = reader.result;
                                const mimeMatch = /^data:(.*);base64,/.exec(
                                  base64
                                );
                                const mimeType = mimeMatch?.[1] || "";

                                controllerField.onChange({
                                  base64,
                                  fileName: file.name,
                                  contentType: mimeType,
                                });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          style={{
                            border: errors?.[field.name]
                              ? "1px solid red"
                              : "2px dashed #ccc",
                            padding: "16px",
                            borderRadius: "8px",
                            backgroundColor: "#fafafa",
                            marginBottom: "12px",
                          }}
                        >
                          <Typography
                            style={{
                              color: errors?.[field.name] ? "red" : "inherit",
                              marginBottom: "8px",
                            }}
                          >
                            {field.label}
                          </Typography>

                          {!hasFile ? (
                            <div className={classes.container}>
                              <label className={classes.uploadLabel}>
                                Upload
                                <input
                                  type="file"
                                  hidden
                                  accept=".pdf,.jpeg,.jpg,.png,.gif,.bmp"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        const base64 = reader.result;
                                        const mimeMatch =
                                          /^data:(.*);base64,/.exec(base64);
                                        const mimeType = mimeMatch?.[1] || "";

                                        controllerField.onChange({
                                          base64,
                                          fileName: file.name,
                                          contentType: mimeType,
                                        });
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </label>
                              <Typography variant="body2">
                                Or drag and drop a file here
                              </Typography>
                            </div>
                          ) : (
                            <div className={classes.fileDisplay}>
                              <div className={classes.fileInfo}>
                                <span className={classes.fileIcon}>
                                  {getFileIcon(value.contentType)}
                                </span>
                                <Typography>{value.fileName}</Typography>
                              </div>
                              <div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const link = document.createElement("a");
                                    link.href = value.base64;
                                    link.download = value.fileName;
                                    link.click();
                                  }}
                                  className={classes.downloadButton}
                                >
                                  <DownloadIcon />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    controllerField.onChange({
                                      base64: "",
                                      fileName: "",
                                      contentType: "",
                                    })
                                  }
                                  className={classes.deleteButton}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          )}

                          {errors?.[field.name] && (
                            <span style={{ color: "red", fontSize: "12px" }}>
                              {errors[field.name].message}
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />
                );
              case "repeatingSection":
                return (
                  <Grid item xs={12}>
                    {/* <Paper
                                            style={{
                                                padding: 24,
                                                border: errors[field.name] ? "2px solid red" : "2px dashed #1976d2",
                                                borderRadius: 8,
                                                backgroundColor: errors[field.name] ? "#ffe6e6" : "#f9f9f9",
                                            }}
                                        > */}
                    <RepeatingGroup
                      key={field.name}
                      config={field}
                      control={control}
                      errors={errors}
                      groupErrors={submitErrors}
                    />
                    {/* </Paper> */}
                  </Grid>
                );
              default:
                return null;
            }
          };

          const isRepeating =
            field.type === "repeatingSection" ||
            field.type === "staticTextBold" ||
            field.type === "staticText";
          return (
            <React.Fragment key={field.name}>
              <Grid item xs={isRepeating ? 12 : 6} key={field.name}>
                {renderField()}
              </Grid>
              {field.conditionalFields &&
                Object.keys(field.conditionalFields).includes(
                  watchedValues?.[field.name]
                ) && (
                  <Grid item xs={12}>
                    <ConditionalFieldRenderer
                      fields={
                        field.conditionalFields[watchedValues[field.name]]
                      }
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                )}
            </React.Fragment>
          );
        })}
      </Grid>
    </>
  );
};

export default FormBuilder;
